import { useRef, useEffect, useState } from "react";

// MapboxGL version to load from CDN
const MAPBOX_VERSION = "3.16.0";
const MAPBOX_CSS_URL = `https://api.mapbox.com/mapbox-gl-js/v${MAPBOX_VERSION}/mapbox-gl.css`;
const MAPBOX_JS_URL = `https://api.mapbox.com/mapbox-gl-js/v${MAPBOX_VERSION}/mapbox-gl.js`;

interface DiveMapProps {
  coordinates?: {
    lat: number;
    lng: number;
  } | null;
  className?: string;
}

// Type for the global mapboxgl object
declare global {
  interface Window {
    mapboxgl: typeof import("mapbox-gl") & { accessToken: string };
  }
}

export function DiveMap({ coordinates, className = "" }: DiveMapProps) {
  const mapRef = useRef<any>(null);
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const markerRef = useRef<any>(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [mapboxLoaded, setMapboxLoaded] = useState(false);
  const [token, setToken] = useState<string | null>(null);
  const [styleUrl, setStyleUrl] = useState<string | null>(null);

  // Load mapbox-gl from CDN (bypasses Bun's CSS processing issues)
  useEffect(() => {
    // Poll for mapboxgl to be available (handles race conditions with StrictMode)
    const checkLoaded = () => {
      if (window.mapboxgl) {
        setMapboxLoaded(true);
        return true;
      }
      return false;
    };

    // Already loaded?
    if (checkLoaded()) return;

    // Add script if not in DOM yet
    if (!document.getElementById("mapbox-gl-script")) {
      // Load CSS
      const link = document.createElement("link");
      link.id = "mapbox-gl-css";
      link.rel = "stylesheet";
      link.href = MAPBOX_CSS_URL;
      document.head.appendChild(link);

      // Load JS
      const script = document.createElement("script");
      script.id = "mapbox-gl-script";
      script.src = MAPBOX_JS_URL;
      document.head.appendChild(script);
    }

    // Poll until loaded (handles StrictMode race conditions)
    const interval = setInterval(() => {
      if (checkLoaded()) {
        clearInterval(interval);
      }
    }, 50);

    return () => clearInterval(interval);
  }, []);

  // Fetch Mapbox config from server
  useEffect(() => {
    fetch("/api/config")
      .then((res) => res.json())
      .then((data) => {
        setToken(data.mapboxToken);
        setStyleUrl(data.mapboxStyleUrl);
      })
      .catch((err) => console.error("Failed to load map config:", err));
  }, []);

  // Initialize map (runs once)
  useEffect(() => {
    if (!mapContainerRef.current || !token || !mapboxLoaded || !window.mapboxgl) return;
    if (mapRef.current) return;

    window.mapboxgl.accessToken = token;

    mapRef.current = new window.mapboxgl.Map({
      container: mapContainerRef.current,
      style: styleUrl || "mapbox://styles/mapbox/satellite-streets-v12",
      center: [0, 20], // Start centered on Atlantic
      zoom: 2,
      projection: "globe",
      attributionControl: false,
      cooperativeGestures: true,
    });

    // Add atmosphere for globe effect
    mapRef.current.on("style.load", () => {
      mapRef.current?.setFog({
        color: "rgb(10, 30, 60)",
        "high-color": "rgb(20, 40, 80)",
        "horizon-blend": 0.1,
        "space-color": "rgb(5, 15, 30)",
        "star-intensity": 0.3,
      });
    });

    mapRef.current.on("load", () => setMapLoaded(true));
  }, [token, styleUrl, mapboxLoaded]);

  // Handle coordinate changes - update marker and fly to location
  useEffect(() => {
    if (!mapRef.current || !mapLoaded || !coordinates || !window.mapboxgl) return;

    // Remove old marker
    if (markerRef.current) {
      markerRef.current.remove();
      markerRef.current = null;
    }

    // Add new marker
    markerRef.current = new window.mapboxgl.Marker()
      .setLngLat([coordinates.lng, coordinates.lat])
      .addTo(mapRef.current);

    // Fly to new location
    mapRef.current.flyTo({
      center: [coordinates.lng, coordinates.lat],
      zoom: 10,
      duration: 2500,
      essential: true,
    });
  }, [coordinates, mapLoaded]);

  return (
    <div className={`h-[300px] sm:h-[400px] rounded-xl overflow-hidden ${className}`}>
      <div
        ref={mapContainerRef}
        className="w-full h-full"
      />
    </div>
  );
}

export default DiveMap;
